import { useState, useEffect, useMemo } from "react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
  IconButton,
  useTheme,
  ThemeProvider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { mergeDarkTheme } from "@aclymatepackages/themes";
import { LoadingButton } from "@aclymatepackages/atoms";

import EditVendorDetails from "./EditVendorDetails";
import EmissionsDetails from "../../EmissionsDetails";
import SettingsObjectDetailsSlider from "../../SettingsObjectDetailsSlider";
import DataSharingTab from "./DataSharingTab";
import ContactsTab from "./ContactsTab";
import ReceivedReportsTab from "./ReceivedReportsTab";

import {
  useOnVendorSave,
  useOnVendorUncategorize,
  useUpdateVendorRelationshipFields,
  isVendorUncategorized,
  archiveVendorTransactions
} from "../../../../../helpers/components/vendors";
import { useCachedDisplayData } from "../../../../../helpers/firebase";
import { analyticsTrack } from "../../../../../helpers/analytics";
import usePlatformLayoutContext from "../../../../../helpers/contexts/platformLayout";
import usePermissions from "../../../../../helpers/hooks/usePermissions";
import { fetchVendorCompanyData } from "../../../../../helpers/utils/apiCalls";

dayjs.extend(utc);

// `closedYears` is built from safe, Dec-31-anchored accounting.date docs
// (bare dayjs() there stays correct), but a transaction's own `date` is an
// arbitrary-day UTC-anchored instant -- dayjs.utc() keeps a
// Jan-1-UTC-midnight transaction attributed to the correct year in a
// timezone behind UTC (e.g. America/Denver), where bare dayjs() would roll
// it back a day into the prior (closed) year and wrongly report it as
// locked. See PRs #2027/#2029/#2083.
export const countLockedTransactions = (transactions, closedYears) =>
  (transactions || [])
    .filter(({ date }) => {
      const txDate = date?.toDate ? date.toDate() : date;
      return closedYears.has(dayjs.utc(txDate).year());
    })
    .reduce((sum, { transactionsCount = 0 }) => sum + transactionsCount, 0);

const EditVendorFooter = ({
  editableVendor,
  setSelectedVendor,
  isSaveEnabled,
  lockedTransactionCount = 0,
  accountingLoading = false
}) => {
  const { id: relationshipId, vendorId } = editableVendor;

  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();
  const { palette } = useTheme();
  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields();
  const { canEdit } = usePermissions();

  const [showLockDialog, setShowLockDialog] = useState(false);

  const { saveLoading, onVendorSave, setSaveLoading } = useOnVendorSave(() =>
    setSelectedVendor(null)
  );
  const { uncategorizeLoading, onVendorUncategorize, setUncategorizeLoading } =
    useOnVendorUncategorize(() => setSelectedVendor(null));

  const showUncategorizeButton = !isVendorUncategorized(editableVendor);

  const onSaveClick = async () => {
    analyticsTrack("Categorized Vendor");
    setSaveLoading(true);
    return await onVendorSave(editableVendor);
  };

  const executeUncategorize = async () => {
    analyticsTrack("Uncategorized Vendor");
    setUncategorizeLoading(true);
    return await onVendorUncategorize(editableVendor);
  };

  const onUncategorizeClick = () => {
    if (lockedTransactionCount > 0) {
      return setShowLockDialog(true);
    }
    return executeUncategorize();
  };

  const onDeleteClick = () => {
    if (relationshipId) {
      updateVendorRelationshipFields(relationshipId, {
        archived: true
      });
    }
    archiveVendorTransactions(vendorId);
    triggerTransactionUpdateCountdown();

    return setSelectedVendor(null);
  };

  const txnWord = lockedTransactionCount === 1 ? "transaction" : "transactions";

  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <IconButton onClick={() => onDeleteClick()} disabled={!canEdit}>
            <DeleteIcon style={{ color: palette.error.main }} />
          </IconButton>
        </Grid>
        <Grid item>
          <Grid container spacing={1} alignItems="center">
            {showUncategorizeButton && (
              <Grid item>
                <LoadingButton
                  isLoading={uncategorizeLoading}
                  onClick={onUncategorizeClick}
                  variant="outlined"
                  color="error"
                  label="Un-categorize"
                  disabled={!canEdit || accountingLoading}
                />
              </Grid>
            )}
            <Grid item>
              <LoadingButton
                isLoading={saveLoading}
                onClick={onSaveClick}
                variant="contained"
                color="primary"
                label="Save Edits"
                disabled={!canEdit || !isSaveEnabled}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Dialog open={showLockDialog} onClose={() => setShowLockDialog(false)}>
        <DialogTitle>Locked Period Transactions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {lockedTransactionCount} {txnWord} for this vendor are in a locked
            accounting period and will keep their existing emissions. All other
            transactions will be zeroed out. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLockDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowLockDialog(false);
              executeUncategorize();
            }}
            color="error"
            variant="contained"
          >
            Un-categorize
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const VendorDetailsSlider = ({
  selectedVendor,
  setSelectedVendor,
  setSelectedTransaction,
  linkedProducts = []
}) => {
  const {
    id,
    transactions,
    name,
    vendorId,
    surveyData,
    vendorContacts = [],
    sharedReports = []
  } = selectedVendor;

  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields();
  const { canEdit } = usePermissions();
  const [accountingYears, accountingLoading] =
    useCachedDisplayData("accounting");
  const closedYears = new Set(
    (accountingYears || [])
      .map(({ date }) => dayjs(date?.toDate ? date.toDate() : date).year())
      .filter(Boolean)
  );

  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [vendorCompanyData, setVendorCompanyData] = useState({});
  const [editableVendor, setEditableVendor] = useState(selectedVendor);
  const { emissionCategory, naicsCode, scopeThreeCategory, description } =
    editableVendor;

  const {
    isStripeCustomer,
    vendorSurveys,
    hasExplorerAccount,
    contactIndividuals = [],
    vendorStartDate
  } = vendorCompanyData;

  const individualsMap = useMemo(
    () =>
      Object.fromEntries(
        contactIndividuals.map(({ id: indId, ...rest }) => [
          indId,
          { id: indId, ...rest }
        ])
      ),
    [contactIndividuals]
  );

  const [localIndividuals, setLocalIndividuals] = useState({});

  const onIndividualResolved = (individualId, data) =>
    setLocalIndividuals((prev) => ({
      ...prev,
      [individualId]: { id: individualId, ...data }
    }));

  const combinedIndividualsMap = { ...individualsMap, ...localIndividuals };

  const resolvedVendorContacts = vendorContacts.map((contact) => ({
    ...contact,
    ...(contact.individualId && combinedIndividualsMap[contact.individualId]
      ? {
          name: combinedIndividualsMap[contact.individualId].name,
          email: combinedIndividualsMap[contact.individualId].email
        }
      : {})
  }));

  useEffect(() => {
    const checkSubscription = async () => {
      setIsCheckingSubscription(true);
      const vendorCompanyData = await fetchVendorCompanyData(vendorId, id);
      setVendorCompanyData(vendorCompanyData);

      if (vendorCompanyData.hasExplorerAccount) {
        updateVendorRelationshipFields(id, {
          hasExplorerAccount: true
        });
      }

      return setIsCheckingSubscription(false);
    };

    if (isCheckingSubscription) {
      checkSubscription();
    }
  }, [
    vendorId,
    isCheckingSubscription,
    id,
    selectedVendor.hasExplorerAccount,
    updateVendorRelationshipFields
  ]);

  const editVendor = (field) => (value) =>
    editObjectData(setEditableVendor, field, value);

  const lockedTransactionCount = countLockedTransactions(
    transactions,
    closedYears
  );

  const isSaveEnabled = () => {
    const findAreAllPropertiesSame = () => {
      const { emissionCategory: originalEmissionCategory } = selectedVendor;
      if (emissionCategory !== "spend-based") {
        return emissionCategory === originalEmissionCategory;
      }

      const {
        naicsCode: originalNaicsCode,
        scopeThreeCategory: originalScopeThreeCategory
      } = selectedVendor;

      return (
        originalNaicsCode === naicsCode &&
        originalScopeThreeCategory === scopeThreeCategory
      );
    };

    const areAllPropertiesSame = findAreAllPropertiesSame();

    const areAllInputsComplete =
      !!emissionCategory &&
      (emissionCategory === "spend-based" ? !!naicsCode : true);

    return !areAllPropertiesSame && areAllInputsComplete;
  };

  const views = [
    {
      label: "Emissions",
      value: "emissions",
      content: (
        <EmissionsDetails
          setSelectedTransaction={setSelectedTransaction}
          emissions={transactions}
          type="vendors"
          name={name}
          chartEmissions={transactions}
          id={vendorId}
        />
      )
    },
    {
      label: "Details",
      value: "details",
      content: (
        <EditVendorDetails
          editVendor={editVendor}
          vendor={editableVendor}
          vendorSurveys={vendorSurveys}
          hasExplorerAccount={hasExplorerAccount}
          disabled={!canEdit}
        />
      ),
      footer: (
        <EditVendorFooter
          editableVendor={editableVendor}
          setSelectedVendor={setSelectedVendor}
          isSaveEnabled={isSaveEnabled()}
          lockedTransactionCount={lockedTransactionCount}
          accountingLoading={accountingLoading}
        />
      )
    },
    {
      label: "Contacts",
      value: "contacts",
      content: (
        <ContactsTab
          vendorId={vendorId}
          relationshipId={id}
          vendorContacts={vendorContacts}
          isCheckingSubscription={isCheckingSubscription}
          individualsMap={combinedIndividualsMap}
          onIndividualResolved={onIndividualResolved}
        />
      )
    },
    {
      label: "Data Sharing",
      value: "data-sharing",
      content: (
        <DataSharingTab
          relationshipId={id}
          isStripeCustomer={isStripeCustomer}
          isCheckingSubscription={isCheckingSubscription}
          vendorSurveys={vendorSurveys}
          surveyData={surveyData}
          linkedProducts={linkedProducts}
          transactions={transactions}
          vendorContacts={resolvedVendorContacts}
          tonsCo2ePerDollar={selectedVendor.tonsCo2ePerDollar}
          vendorStartDate={vendorStartDate}
        />
      )
    },
    ...((isStripeCustomer || sharedReports.length > 0) &&
    !isCheckingSubscription
      ? [
          {
            label: "Reports",
            value: "reports",
            content: <ReceivedReportsTab sharedReports={sharedReports} />
          }
        ]
      : [])
  ];

  const footerInputSubcategories = [
    { subcategory: emissionCategory },
    ...(emissionCategory === "spend-based"
      ? []
      : [{ subcategory: "spend-based" }])
  ];

  return (
    <SettingsObjectDetailsSlider
      type="vendors"
      settingsObject={selectedVendor}
      editNameDisabled={!canEdit}
      onNameSave={(name) => {
        editVendor("name")(name);
        const { id: relationshipId, canonicalName } = selectedVendor;

        if (relationshipId) {
          const nickname = name !== canonicalName ? name : null;
          return updateVendorRelationshipFields(relationshipId, {
            nickname
          });
        }
      }}
      views={views}
      setSelectedObject={setSelectedVendor}
      footerInputSubcategories={footerInputSubcategories}
      leftContent={
        description && (
          <ThemeProvider theme={mergeDarkTheme}>
            <Container maxWidth="sm">
              <Typography variant="h5" color="textPrimary" align="center">
                {description}
              </Typography>
            </Container>
          </ThemeProvider>
        )
      }
    />
  );
};
export default VendorDetailsSlider;
